import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Shell, KpiCard, Section, StatusPill } from "@/components/loomi/Layout";
import { PM_NAV } from "./PmDashboard";
import { api, formatApiError } from "@/lib/api";
import { CopyField } from "@/components/loomi/CopyField";
import { LogsTable, SessionsTable } from "../admin/AdminProjectDetail";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import { Info, Send, CheckCircle2 } from "lucide-react";

export default function PmProjectDetail() {
  const { pid } = useParams();
  const [p, setP] = useState(null);
  const [logs, setLogs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [surveyUrl, setSurveyUrl] = useState("");
  const [linking, setLinking] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pj, lg, ss] = await Promise.all([
        api.get(`/projects/${pid}`),
        api.get(`/projects/${pid}/logs`),
        api.get(`/projects/${pid}/sessions`),
      ]);
      setP(pj.data); setLogs(lg.data); setSessions(ss.data);
      setSurveyUrl(pj.data.survey_url || "");
    } catch (e) { toast.error("Load failed", { description: formatApiError(e) }); }
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (s) => {
    try { await api.patch(`/projects/${pid}/status`, { status: s }); toast.success(`Project ${s}`); load(); }
    catch (e) { toast.error("Failed", { description: formatApiError(e) }); }
  };

  const linkSurvey = async () => {
    if (!surveyUrl.trim()) return toast.error("Paste the survey link from your client first");
    setLinking(true);
    try {
      await api.patch(`/projects/${pid}/survey-url`, { survey_url: surveyUrl.trim() });
      toast.success("Survey URL saved — project is now live");
      load();
    } catch (e) { toast.error("Failed", { description: formatApiError(e) }); }
    finally { setLinking(false); }
  };

  if (!p) return <Shell title="Loading…" portalLabel="PM" nav={PM_NAV}><div className="text-mute" data-testid="pm-detail-loading">Loading…</div></Shell>;
  const c = p.counters || {};
  const isDraft = p.status === "draft";
  const urls = p.urls || {};

  return (
    <Shell title={p.name} portalLabel="PM" nav={PM_NAV}>
      <div className="flex items-center gap-3 mb-6">
        <StatusPill status={p.status} />
        <span className="text-xs uppercase tracking-widest text-mute">{p.type}</span>
        <div className="ml-auto flex gap-2">
          {["live","paused","closed"].map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`pm-set-status-${s}`}
              disabled={s === "live" && isDraft}
              title={s === "live" && isDraft ? "Set the survey URL first" : ""}
              className={`text-xs px-3 py-1.5 rounded-md border ${p.status===s ? "bg-indigo-500/20 border-indigo-400/50" : "bg-white/5 border-white/10 hover:bg-white/10"} ${s==="live"&&isDraft?"opacity-40 cursor-not-allowed":""}`}>{s}</button>
          ))}
        </div>
      </div>

      {isDraft && (
        <div className="mb-6 rounded-2xl p-5 bg-slate-500/10 border border-slate-500/30 flex items-start gap-3" data-testid="pm-draft-banner">
          <Info className="w-5 h-5 text-slate-300 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-slate-200 leading-relaxed">
            <b>Draft project.</b> Share the redirect URLs below with your client so they configure them on their survey platform. Once they return the survey link, paste it in <b>Step 2</b> to auto-flip the project to <b>live</b>.
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Starts" value={c.starts || 0} accent="blue" testId="pmpd-starts" />
        <KpiCard label="Complete" value={c.complete || 0} accent="green" testId="pmpd-complete" />
        <KpiCard label="Terminate" value={c.terminate || 0} accent="red" testId="pmpd-terminate" />
        <KpiCard label="Overquota" value={c.overquota || 0} accent="amber" testId="pmpd-overquota" />
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-8">
        <Section title="Share with your client" testId="section-pm-share">
          <div className="text-[10px] uppercase tracking-widest text-mute mb-1">Entry URL</div>
          <CopyField value={urls.entry} testId="pm-entry-url" />
          {urls.complete && (
            <div className="mt-4 space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-mute mb-1">Complete callback</div>
                <CopyField value={urls.complete} testId="pm-complete-url" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-mute mb-1">Terminate callback</div>
                <CopyField value={urls.terminate} testId="pm-terminate-url" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-mute mb-1">Over-quota callback</div>
                <CopyField value={urls.overquota} testId="pm-overquota-url" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-mute mb-1">Security-term callback</div>
                <CopyField value={urls.security_term} testId="pm-security-url" />
              </div>
            </div>
          )}
          <div className="flex gap-4 items-center mt-4">
            <div className="bg-white p-3 rounded-md" data-testid="pm-qr"><QRCode value={urls.entry || "https://synclineresearch.com"} size={110} /></div>
            <div className="text-xs text-mute leading-relaxed">Share via WhatsApp, SMS, email, or print the QR. Every click is tracked, signed, and idempotent.</div>
          </div>
        </Section>

        <Section
          title={isDraft ? "Step 2 — Add the survey URL from your client" : "Survey URL"}
          testId="section-pm-survey-url"
        >
          <div className="text-[10px] uppercase tracking-widest text-mute mb-1">Survey URL</div>
          <div className="flex flex-col md:flex-row gap-3">
            <input
              data-testid="pm-survey-url-input"
              value={surveyUrl}
              onChange={(e) => setSurveyUrl(e.target.value)}
              placeholder="https://survey.decipherinc.com/survey/…"
              className="flex-1 bg-black/50 border border-white/10 rounded-md px-3 py-2.5 text-sm font-mono focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
            />
            <button
              onClick={linkSurvey}
              disabled={linking || (!isDraft && surveyUrl === (p.survey_url || ""))}
              data-testid="pm-survey-url-submit"
              className={`px-4 py-2.5 rounded-md text-sm font-semibold flex items-center justify-center gap-2 ${isDraft ? "bg-emerald-500 hover:bg-emerald-400 text-black shadow-[0_0_28px_rgba(16,185,129,0.35)]" : "bg-indigo-500 hover:bg-indigo-400"} disabled:opacity-60`}
            >
              <Send className="w-4 h-4" />
              {linking ? "Saving…" : (isDraft ? "Save & go live" : "Update")}
            </button>
          </div>
          {!isDraft && p.survey_url && (
            <div className="mt-3 flex items-center gap-2 text-emerald-300 text-xs" data-testid="pm-survey-url-live">
              <CheckCircle2 className="w-3.5 h-3.5" /> Live — respondents are being routed to this URL.
            </div>
          )}
          <p className="text-xs text-mute mt-3 leading-relaxed">The URL where SyncLine forwards respondents after they hit the entry redirect. Your client configures the callback URLs on the left; you configure this survey URL here.</p>
        </Section>
      </div>

      <div className="mt-6">
        <Section title="Sessions" testId="section-pm-sessions">
          <SessionsTable sessions={sessions} />
        </Section>
      </div>
      <div className="mt-6">
        <Section title="Redirect logs" testId="section-pm-logs">
          <LogsTable logs={logs} />
        </Section>
      </div>
      <div className="mt-6 text-xs text-mute">
        <Link to="/pm" className="hover:text-white" data-testid="pm-back-overview">← back to overview</Link>
      </div>
    </Shell>
  );
}
